import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const FeaturedCarousel = ({ posts }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === posts.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [posts.length, isAutoPlaying]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? posts.length - 1 : currentIndex - 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === posts.length - 1 ? 0 : currentIndex + 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!posts || posts.length === 0) return null;

  const currentPost = posts[currentIndex];

  return (
    <div className="relative bg-surface rounded-lg overflow-hidden card-shadow-lg">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-96">
        {/* Image Section */}
        <div className="relative overflow-hidden">
          <Image
            src={currentPost.image}
            alt={currentPost.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent lg:hidden"></div>
          
          {/* Navigation Arrows */}
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all duration-150 shadow-md"
          >
            <Icon name="ChevronLeft" size={20} className="text-text-primary" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all duration-150 shadow-md"
          >
            <Icon name="ChevronRight" size={20} className="text-text-primary" />
          </button>
        </div>

        {/* Content Section */}
        <div className="p-8 flex flex-col justify-center">
          <div className="flex items-center space-x-2 mb-4">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent text-white">
              <Icon name="Star" size={12} className="mr-1" />
              Featured
            </span>
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
              {currentPost.category}
            </span>
          </div>

          <h3 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary mb-4 line-clamp-2">
            {currentPost.title}
          </h3>

          <p className="text-text-secondary mb-6 line-clamp-3">
            {currentPost.excerpt}
          </p>

          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full overflow-hidden">
                <Image
                  src={currentPost.author.avatar}
                  alt={currentPost.author.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">
                  {currentPost.author.name}
                </p>
                <p className="text-xs text-text-secondary">
                  {formatDate(currentPost.publishedAt)} • {currentPost.readTime}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button className="btn-primary">
              Read Article
            </button>
            <div className="flex items-center space-x-4 text-sm text-text-secondary">
              <div className="flex items-center space-x-1">
                <Icon name="Eye" size={16} />
                <span>{currentPost.views}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="MessageCircle" size={16} />
                <span>{currentPost.comments}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dots Indicator */}
      <div className="absolute bottom-4 left-1/4 transform -translate-x-1/2 flex space-x-2">
        {posts.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-150 ${
              index === currentIndex
                ? 'bg-white w-6' :'bg-white/50 hover:bg-white/75'
            }`}
          />
        ))}
      </div>

      {/* Auto-play indicator */}
      <div className="absolute top-4 right-4">
        <button
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
          className="w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all duration-150 shadow-md"
        >
          <Icon 
            name={isAutoPlaying ? "Pause" : "Play"} 
            size={14} 
            className="text-text-primary" 
          />
        </button>
      </div>
    </div>
  );
};

export default FeaturedCarousel;